import React from 'react';
import { AlertTriangle, TrendingDown } from 'lucide-react';

/**
 * Floating Loss Indicator Component
 * Shows the current floating loss percentage with visual warnings
 * as traders approach the 2% limit (or custom limit)
 */
const FloatingLossIndicator = ({ 
  floatingLossPercent = 0, 
  maxFloatingLoss = 2,
  showLabel = true,
  size = 'default' // 'small', 'default', 'large'
}) => {
  const percentage = Math.abs(floatingLossPercent);
  const percentOfLimit = (percentage / maxFloatingLoss) * 100;
  
  // Determine severity level
  const getSeverity = () => {
    if (percentage >= maxFloatingLoss) return 'critical';  // Breached
    if (percentOfLimit >= 80) return 'danger';             // 80%+ of limit
    if (percentOfLimit >= 50) return 'warning';            // 50-80% of limit
    return 'safe';                                          // Under 50%
  };
  
  const severity = getSeverity();
  
  // Color schemes based on severity
  const colors = {
    safe: {
      bg: 'bg-green-500/10',
      border: 'border-green-500/30',
      text: 'text-green-400',
      progress: 'bg-green-500',
      icon: 'text-green-400'
    },
    warning: {
      bg: 'bg-yellow-500/10',
      border: 'border-yellow-500/30',
      text: 'text-yellow-400',
      progress: 'bg-yellow-500',
      icon: 'text-yellow-400'
    },
    danger: {
      bg: 'bg-orange-500/10',
      border: 'border-orange-500/30',
      text: 'text-orange-400',
      progress: 'bg-orange-500',
      icon: 'text-orange-400'
    },
    critical: {
      bg: 'bg-red-500/20',
      border: 'border-red-500/50',
      text: 'text-red-400',
      progress: 'bg-red-500',
      icon: 'text-red-400'
    }
  };
  
  const colorScheme = colors[severity];
  
  // Size classes
  const sizeClasses = {
    small: {
      container: 'p-2',
      text: 'text-xs',
      icon: 'w-3 h-3',
      progress: 'h-1',
      value: 'text-sm'
    },
    default: {
      container: 'p-3',
      text: 'text-sm',
      icon: 'w-4 h-4',
      progress: 'h-1.5',
      value: 'text-base'
    },
    large: {
      container: 'p-4',
      text: 'text-base',
      icon: 'w-5 h-5',
      progress: 'h-2',
      value: 'text-lg'
    }
  };
  
  const sizeScheme = sizeClasses[size];
  
  // If no floating loss, show minimal indicator
  if (percentage === 0) {
    return (
      <div 
        className={`${sizeScheme.container} rounded-lg ${colors.safe.bg} border ${colors.safe.border}`}
        data-testid="floating-loss-indicator"
      >
        <div className="flex items-center justify-between">
          {showLabel && (
            <span className={`${sizeScheme.text} text-gray-400`}>Floating Loss</span>
          )}
          <span className={`font-mono font-medium ${colors.safe.text} ${sizeScheme.value}`}>
            0.00%
          </span>
        </div>
      </div>
    );
  }
  
  return (
    <div 
      className={`${sizeScheme.container} rounded-lg ${colorScheme.bg} border ${colorScheme.border} ${
        severity === 'critical' ? 'animate-pulse' : ''
      }`}
      data-testid="floating-loss-indicator"
    >
      {/* Header */}
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          {severity === 'critical' || severity === 'danger' ? (
            <AlertTriangle className={`${sizeScheme.icon} ${colorScheme.icon}`} />
          ) : (
            <TrendingDown className={`${sizeScheme.icon} ${colorScheme.icon}`} />
          )}
          {showLabel && (
            <span className={`${sizeScheme.text} text-gray-400`}>
              Floating Loss
            </span>
          )}
        </div>
        <span className={`font-mono font-bold ${colorScheme.text} ${sizeScheme.value}`}>
          -{percentage.toFixed(2)}%
        </span>
      </div>
      
      {/* Progress Bar */}
      <div className={`${sizeScheme.progress} bg-white/10 rounded-full overflow-hidden`}>
        <div 
          className={`${sizeScheme.progress} ${colorScheme.progress} rounded-full transition-all duration-300`}
          style={{ width: `${Math.min(percentOfLimit, 100)}%` }}
        />
      </div>
      
      {/* Status Message */}
      <div className="flex items-center justify-between mt-2">
        <span className={`${sizeScheme.text} text-gray-500`}>
          Limit: {maxFloatingLoss}%
        </span>
        {severity === 'critical' && (
          <span className={`${sizeScheme.text} ${colorScheme.text} font-medium`}>
            BREACH
          </span>
        )}
        {severity === 'danger' && (
          <span className={`${sizeScheme.text} ${colorScheme.text} font-medium`}>
            DANGER ZONE
          </span>
        )}
        {severity === 'warning' && (
          <span className={`${sizeScheme.text} ${colorScheme.text} font-medium`}>
            Caution
          </span>
        )}
        {severity === 'safe' && (
          <span className={`${sizeScheme.text} text-gray-500`}>
            {percentOfLimit.toFixed(0)}% of limit
          </span>
        )}
      </div>
    </div>
  );
};

export default FloatingLossIndicator;
